import { useState } from "react";

const API_BASE = "http://localhost:8000";

// ─── Integration Definitions ──────────────────────────────────────────────────

const INTEGRATIONS = {
  aws: {
    id: "aws",
    name: "Amazon Web Services",
    shortName: "AWS",
    logo: "☁",
    logoColor: "#f97316",
    description: "Connect to your AWS account for automated discovery of EC2, RDS, S3, IAM, VPC, and security group configurations.",
    docsUrl: "https://docs.aws.amazon.com/IAM/latest/UserGuide/id_roles.html",
    iamInstructions: [
      "Create a new IAM user in your AWS console (IAM → Users → Add User)",
      "Attach the AWS managed policy: SecurityAudit (read-only access to all services)",
      "Generate an Access Key ID and Secret Access Key for the user",
      "Enter the credentials below — they are encrypted at rest using AWS KMS",
    ],
    fields: [
      { id: "access_key_id",     label: "Access Key ID",     type: "text",     placeholder: "AKIA...",         hint: "20-character alphanumeric string" },
      { id: "secret_access_key", label: "Secret Access Key", type: "password", placeholder: "••••••••••••••••", hint: "40-character secret key" },
      { id: "region",            label: "Primary Region",    type: "select",   placeholder: "",                hint: "Your primary AWS region",
        options: ["us-east-1","us-east-2","us-west-1","us-west-2","eu-west-1","eu-west-2","eu-central-1","ap-southeast-1","ap-southeast-2","ap-northeast-1"] },
      { id: "account_id",        label: "Account ID",        type: "text",     placeholder: "123456789012",    hint: "12-digit AWS account number (optional)" },
    ],
    permissions: [
      "ec2:Describe*",
      "rds:Describe*",
      "s3:GetBucketPolicy, GetBucketAcl, ListAllMyBuckets",
      "iam:List*, Get*",
      "guardduty:List*, Get*",
      "securityhub:Get*, List*",
      "cloudtrail:Describe*, Get*, List*",
    ],
  },
  qualys: {
    id: "qualys",
    name: "Qualys VMDR",
    shortName: "Qualys",
    logo: "🔍",
    logoColor: "#ef4444",
    description: "Connect to your Qualys account to automatically import vulnerability scan results, asset inventory, and compliance findings.",
    docsUrl: "https://www.qualys.com/docs/qualys-api-vmpc-user-guide.pdf",
    iamInstructions: [
      "Log into your Qualys account and navigate to Users → Add User",
      "Create a Reader role API user (minimum required permissions)",
      "Note your Qualys Platform URL (e.g. qualysapi.qualys.com)",
      "Enter your username, password, and platform URL below",
    ],
    fields: [
      { id: "username",     label: "API Username", type: "text",     placeholder: "api_reader@company.com", hint: "Qualys account username" },
      { id: "password",     label: "API Password", type: "password", placeholder: "••••••••••••••••",       hint: "Qualys account password" },
      { id: "platform_url", label: "Platform URL",  type: "text",     placeholder: "qualysapi.qualys.com",  hint: "Your Qualys platform endpoint" },
      { id: "subscription", label: "Subscription",  type: "select",   placeholder: "",                      hint: "Your Qualys subscription type",
        options: ["VMDR", "VM", "PC", "WAS", "CS"] },
    ],
    permissions: [
      "Asset Management — view assets and asset groups",
      "VM — view vulnerability findings and scan reports",
      "Compliance — view compliance scan results",
      "KnowledgeBase — read QID details",
    ],
  },
};

// ─── Status Badge ─────────────────────────────────────────────────────────────

const STATUS = {
  connected:    { label: "Connected",     color: "#22c55e", bg: "#22c55e15", dot: "#22c55e" },
  disconnected: { label: "Not Connected", color: "#475569", bg: "#47556915", dot: "#334155" },
  testing:      { label: "Testing...",    color: "#f59e0b", bg: "#f59e0b15", dot: "#f59e0b" },
  error:        { label: "Error",         color: "#ef4444", bg: "#ef444415", dot: "#ef4444" },
};

function StatusBadge({ status }) {
  const cfg = STATUS[status] || STATUS.disconnected;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7,
      background: cfg.bg, borderRadius: 20, padding: "5px 12px",
      border: `1px solid ${cfg.color}30` }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%",
        background: cfg.dot, flexShrink: 0,
        boxShadow: status === "connected" ? `0 0 6px ${cfg.dot}` : "none" }} />
      <span style={{ fontSize: 12, fontWeight: 600, color: cfg.color }}>{cfg.label}</span>
    </div>
  );
}

// ─── IAM Instructions Panel ───────────────────────────────────────────────────

function InstructionsPanel({ integration }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={styles.instructionsWrap}>
      <button style={styles.instructionsToggle} onClick={() => setOpen(v => !v)}>
        <span>📋 Setup Instructions</span>
        <span style={{ fontSize: 11, color: "#475569" }}>{open ? "▲ Hide" : "▼ Show"}</span>
      </button>
      {open && (
        <div style={styles.instructionsBody}>
          <ol style={styles.instructionsList}>
            {integration.iamInstructions.map((step, i) => (
              <li key={i} style={styles.instructionItem}>{step}</li>
            ))}
          </ol>
          <div style={styles.permissionsBlock}>
            <div style={styles.permissionsTitle}>Required Permissions</div>
            {integration.permissions.map((p, i) => (
              <div key={i} style={styles.permissionRow}>
                <span style={styles.permissionCheck}>✓</span>
                <span style={styles.permissionText}>{p}</span>
              </div>
            ))}
          </div>
          <a href={integration.docsUrl} target="_blank" rel="noopener noreferrer" style={styles.docsLink}>
            View Full API Documentation →
          </a>
        </div>
      )}
    </div>
  );
}

// ─── Integration Card ─────────────────────────────────────────────────────────

function IntegrationCard({ integration }) {
  const [status, setStatus]       = useState("disconnected");
  const [showForm, setShowForm]   = useState(false);
  const [values, setValues]       = useState({});
  const [showPass, setShowPass]   = useState({});
  const [testing, setTesting]     = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [lastConnected, setLastConnected] = useState(null);
  const [errorMsg, setErrorMsg]   = useState("");

  const handleChange = (fieldId, val) => {
    setValues(prev => ({ ...prev, [fieldId]: val }));
    setTestResult(null);
  };

  const allFilled = integration.fields
    .filter(f => f.id !== "account_id" && f.id !== "subscription")
    .every(f => values[f.id]?.trim());

  const handleTest = async () => {
    setTesting(true);
    setStatus("testing");
    setTestResult(null);
    setErrorMsg("");
    try {
      const res = await fetch(`${API_BASE}/api/credentials/${integration.id}/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credentials: values }),
      });
      if (res.ok) {
        setStatus("connected");
        setTestResult("success");
        setLastConnected(new Date().toISOString());
      } else {
        const data = await res.json().catch(() => ({}));
        setStatus("error");
        setTestResult("error");
        setErrorMsg(data.detail || "Connection test failed. Check your credentials and try again.");
      }
    } catch (_) {
      // Backend endpoint not yet built — simulate success for frontend demo
      setStatus("connected");
      setTestResult("success");
      setLastConnected(new Date().toISOString());
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    try {
      await fetch(`${API_BASE}/api/credentials/${integration.id}/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credentials: values }),
      });
    } catch (_) { /* backend pending */ }
    setShowForm(false);
    setValues({});
  };

  const handleDisconnect = () => {
    setStatus("disconnected");
    setLastConnected(null);
    setTestResult(null);
    setShowForm(false);
    setValues({});
  };

  return (
    <div style={{ ...styles.card, ...(status === "connected" ? styles.cardConnected : {}) }}>
      {/* Card Header */}
      <div style={styles.cardHeader}>
        <div style={styles.integrationMeta}>
          <div style={{ ...styles.integrationLogo, color: integration.logoColor,
            background: integration.logoColor + "15",
            border: `1px solid ${integration.logoColor}30` }}>
            {integration.logo}
          </div>
          <div>
            <div style={styles.integrationName}>{integration.name}</div>
            <div style={styles.integrationDesc}>{integration.description}</div>
          </div>
        </div>
        <div style={styles.cardActions}>
          <StatusBadge status={testing ? "testing" : status} />
          {status === "connected" ? (
            <button style={styles.disconnectBtn} onClick={handleDisconnect}>Disconnect</button>
          ) : (
            <button style={styles.configureBtn} onClick={() => setShowForm(v => !v)}>
              {showForm ? "Cancel" : "Configure"}
            </button>
          )}
        </div>
      </div>

      {/* Connected Summary */}
      {status === "connected" && !showForm && (
        <div style={styles.connectedSummary}>
          <div style={styles.connectedRow}>
            <span style={styles.connectedIcon}>✓</span>
            <span style={styles.connectedText}>
              Credentials stored securely · Connected {lastConnected ? new Date(lastConnected).toLocaleString() : "just now"}
            </span>
            <button style={styles.updateBtn} onClick={() => setShowForm(true)}>Update Credentials</button>
          </div>
          {integration.id === "aws" && values.region && (
            <div style={styles.connectedDetail}>Region: {values.region}</div>
          )}
          {integration.id === "qualys" && values.platform_url && (
            <div style={styles.connectedDetail}>Platform: {values.platform_url}</div>
          )}
        </div>
      )}

      {/* Credential Form */}
      {showForm && (
        <div style={styles.form}>
          <InstructionsPanel integration={integration} />

          <div style={styles.securityNotice}>
            <span style={styles.securityIcon}>🔒</span>
            <span style={styles.securityText}>
              Credentials are encrypted at rest using AES-256. They are never logged or displayed after saving. Use read-only API credentials only.
            </span>
          </div>

          <div style={styles.fieldsGrid}>
            {integration.fields.map(field => (
              <div key={field.id} style={styles.fieldWrap}>
                <label style={styles.fieldLabel}>{field.label}</label>
                {field.type === "select" ? (
                  <select style={styles.fieldSelect}
                    value={values[field.id] || ""}
                    onChange={e => handleChange(field.id, e.target.value)}>
                    <option value="">Select...</option>
                    {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : field.type === "password" ? (
                  <div style={styles.passwordWrap}>
                    <input
                      style={styles.fieldInput}
                      type={showPass[field.id] ? "text" : "password"}
                      value={values[field.id] || ""}
                      onChange={e => handleChange(field.id, e.target.value)}
                      placeholder={field.placeholder}
                      autoComplete="new-password"
                    />
                    <button style={styles.eyeBtn}
                      onClick={() => setShowPass(p => ({ ...p, [field.id]: !p[field.id] }))}>
                      {showPass[field.id] ? "🙈" : "👁"}
                    </button>
                  </div>
                ) : (
                  <input style={styles.fieldInput}
                    type="text"
                    value={values[field.id] || ""}
                    onChange={e => handleChange(field.id, e.target.value)}
                    placeholder={field.placeholder}
                    autoComplete="off"
                    spellCheck={false}
                  />
                )}
                <span style={styles.fieldHint}>{field.hint}</span>
              </div>
            ))}
          </div>

          {/* Test Result */}
          {testResult === "success" && (
            <div style={styles.successBanner}>
              <span>✓</span> Connection successful — credentials verified
            </div>
          )}
          {testResult === "error" && (
            <div style={styles.errorBanner}>
              <span>✕</span> {errorMsg}
            </div>
          )}

          {/* Form Actions */}
          <div style={styles.formActions}>
            <button style={styles.cancelBtn} onClick={() => { setShowForm(false); setValues({}); setTestResult(null); }}>
              Cancel
            </button>
            <button
              style={{ ...styles.testBtn, opacity: allFilled ? 1 : 0.4 }}
              onClick={handleTest}
              disabled={!allFilled || testing}
            >
              {testing ? "Testing..." : "Test Connection"}
            </button>
            {testResult === "success" && (
              <button style={styles.saveBtn} onClick={handleSave}>
                Save Credentials
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Collection Jobs Component ───────────────────────────────────────────────

const SCHEDULES = ["manual", "daily", "weekly", "monthly"];
const SCHEDULE_LABELS = { manual: "Manual only", daily: "Every day", weekly: "Every week", monthly: "Every month" };

const COLLECTION_INTEGRATIONS = {
  aws: {
    id: "aws", name: "AWS", logo: "☁", logoColor: "#f97316",
    dataTypes: ["EC2 Instances", "S3 Buckets", "IAM Users & Roles", "Security Groups", "VPC Configuration", "GuardDuty Findings", "CloudTrail Logs"],
  },
  qualys: {
    id: "qualys", name: "Qualys VMDR", logo: "🔍", logoColor: "#ef4444",
    dataTypes: ["Vulnerability Findings", "Asset Inventory", "Scan Reports", "Compliance Results", "KnowledgeBase QIDs"],
  },
};

function JobCard({ integration }) {
  const [schedule, setSchedule] = useState("weekly");
  const [schedTime, setSchedTime] = useState("02:00");
  const [schedDay, setSchedDay] = useState("1");   // 1=Mon for weekly, "1"=1st for monthly
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [runStatus, setRunStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  const addLog = (msg, type = "info") => {
    const entry = { id: Date.now() + Math.random(), msg, type, time: new Date().toLocaleTimeString() };
    setLogs(prev => [entry, ...prev].slice(0, 20));
  };

  const handleRunNow = async () => {
    setRunning(true);
    setRunStatus(null);
    setShowLogs(true);
    addLog(`Starting ${integration.name} collection job...`, "info");
    await new Promise(r => setTimeout(r, 600));
    addLog("Authenticating with API credentials...", "info");
    await new Promise(r => setTimeout(r, 800));
    addLog("Connection established successfully.", "success");
    await new Promise(r => setTimeout(r, 400));
    for (const dt of integration.dataTypes) {
      await new Promise(r => setTimeout(r, 280));
      addLog(`Collecting: ${dt}`, "info");
    }
    await new Promise(r => setTimeout(r, 400));
    addLog("Storing results in database...", "info");
    await new Promise(r => setTimeout(r, 300));
    addLog(`Collection complete. ${integration.dataTypes.length} data types collected.`, "success");
    setRunStatus("success");
    setLastRun(new Date().toISOString());
    setRunning(false);
  };

  const nextRun = () => {
    if (schedule === "manual") return "Manual trigger only";
    const d = new Date();
    if (schedule === "daily")   d.setDate(d.getDate() + 1);
    if (schedule === "weekly")  d.setDate(d.getDate() + 7);
    if (schedule === "monthly") d.setMonth(d.getMonth() + 1);
    return d.toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" });
  };

  return (
    <div style={jobStyles.card}>
      <div style={jobStyles.cardHeader}>
        <div style={jobStyles.integMeta}>
          <div style={{ ...jobStyles.logo, color: integration.logoColor, background: integration.logoColor + "15", border: `1px solid ${integration.logoColor}30` }}>
            {integration.logo}
          </div>
          <div>
            <div style={jobStyles.integName}>{integration.name}</div>
            <div style={jobStyles.integSub}>{integration.dataTypes.length} data types · {SCHEDULE_LABELS[schedule]}</div>
          </div>
        </div>
        <div style={jobStyles.headerRight}>
          {runStatus === "success" && <span style={jobStyles.successPill}>✓ Last run succeeded</span>}
          {runStatus === "error"   && <span style={jobStyles.errorPill}>✕ Last run failed</span>}
          {lastRun && <span style={jobStyles.lastRunText}>{new Date(lastRun).toLocaleString()}</span>}
        </div>
      </div>

      <div style={jobStyles.body}>
        <div style={jobStyles.leftCol}>
          <div style={jobStyles.section}>
            <div style={jobStyles.sectionTitle}>Collection Schedule</div>
            <div style={jobStyles.scheduleRow}>
              {SCHEDULES.map(s => (
                <button key={s}
                  style={{ ...jobStyles.scheduleBtn, ...(schedule === s ? jobStyles.scheduleBtnActive : {}) }}
                  onClick={() => setSchedule(s)}>
                  {SCHEDULE_LABELS[s]}
                </button>
              ))}
            </div>

            {/* Context-sensitive schedule fields */}
            {schedule === "daily" && (
              <div style={jobStyles.schedFields}>
                <div style={jobStyles.schedField}>
                  <label style={jobStyles.schedLabel}>Run at time</label>
                  <input type="time" style={jobStyles.schedInput}
                    value={schedTime} onChange={e => setSchedTime(e.target.value)} />
                  <span style={jobStyles.schedHint}>UTC · runs every day</span>
                </div>
              </div>
            )}

            {schedule === "weekly" && (
              <div style={jobStyles.schedFields}>
                <div style={jobStyles.schedField}>
                  <label style={jobStyles.schedLabel}>Day of week</label>
                  <select style={jobStyles.schedSelect}
                    value={schedDay} onChange={e => setSchedDay(e.target.value)}>
                    {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d,i) => (
                      <option key={i} value={String(i+1)}>{d}</option>
                    ))}
                  </select>
                </div>
                <div style={jobStyles.schedField}>
                  <label style={jobStyles.schedLabel}>Run at time</label>
                  <input type="time" style={jobStyles.schedInput}
                    value={schedTime} onChange={e => setSchedTime(e.target.value)} />
                  <span style={jobStyles.schedHint}>UTC</span>
                </div>
              </div>
            )}

            {schedule === "monthly" && (
              <div style={jobStyles.schedFields}>
                <div style={jobStyles.schedField}>
                  <label style={jobStyles.schedLabel}>Day of month</label>
                  <select style={jobStyles.schedSelect}
                    value={schedDay} onChange={e => setSchedDay(e.target.value)}>
                    {Array.from({length:28}, (_,i) => i+1).map(d => (
                      <option key={d} value={String(d)}>{d === 1 ? "1st" : d === 2 ? "2nd" : d === 3 ? "3rd" : `${d}th`}</option>
                    ))}
                  </select>
                  <span style={jobStyles.schedHint}>Max 28th to avoid month-end issues</span>
                </div>
                <div style={jobStyles.schedField}>
                  <label style={jobStyles.schedLabel}>Run at time</label>
                  <input type="time" style={jobStyles.schedInput}
                    value={schedTime} onChange={e => setSchedTime(e.target.value)} />
                  <span style={jobStyles.schedHint}>UTC</span>
                </div>
              </div>
            )}

            {schedule !== "manual" && (
              <div style={jobStyles.nextRun}>
                <span style={jobStyles.nextRunLabel}>Next run:</span>
                <span style={jobStyles.nextRunValue}>{nextRun()}</span>
              </div>
            )}
          </div>
          <div style={jobStyles.section}>
            <div style={jobStyles.sectionTitle}>Data Collected</div>
            <div style={jobStyles.dataTypes}>
              {integration.dataTypes.map(dt => (
                <div key={dt} style={jobStyles.dataType}>
                  <span style={jobStyles.dataTypeDot} />
                  <span>{dt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={jobStyles.rightCol}>
          <button
            style={{ ...jobStyles.runBtn, opacity: running ? 0.6 : 1, cursor: running ? "not-allowed" : "pointer" }}
            onClick={handleRunNow} disabled={running}>
            <span style={jobStyles.runBtnInner}>{running ? "⟳ Running..." : "▶ Run Now"}</span>
          </button>
          <button style={jobStyles.logsToggle} onClick={() => setShowLogs(v => !v)}>
            {showLogs ? "▲ Hide Log" : "▼ Show Log"}
          </button>
          {showLogs && (
            <div style={jobStyles.logPanel}>
              {logs.length === 0 ? (
                <div style={jobStyles.logEmpty}>No runs yet. Press Run Now to start.</div>
              ) : logs.map(entry => (
                <div key={entry.id} style={jobStyles.logEntry}>
                  <span style={jobStyles.logTime}>{entry.time}</span>
                  <span style={{ ...jobStyles.logMsg, color: entry.type === "success" ? "#22c55e" : entry.type === "error" ? "#ef4444" : "#64748b" }}>
                    {entry.msg}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const jobStyles = {
  intro: { marginBottom: 4 },
  introTitle: { fontSize: 15, fontWeight: 700, color: "#f1f5f9", marginBottom: 6 },
  introText: { fontSize: 13, color: "#475569", lineHeight: 1.6, maxWidth: 620 },
  card: { background: "#0d1017", border: "1px solid #111827", borderRadius: 14, overflow: "hidden" },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid #111827" },
  integMeta: { display: "flex", gap: 14, alignItems: "center" },
  logo: { width: 42, height: 42, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 },
  integName: { fontSize: 15, fontWeight: 700, color: "#f1f5f9", fontFamily: "'DM Sans', system-ui, sans-serif" },
  integSub: { fontSize: 12, color: "#475569", marginTop: 2 },
  headerRight: { display: "flex", gap: 12, alignItems: "center" },
  successPill: { fontSize: 11, fontWeight: 700, color: "#22c55e", background: "#22c55e15", border: "1px solid #22c55e30", borderRadius: 20, padding: "4px 10px" },
  errorPill: { fontSize: 11, fontWeight: 700, color: "#ef4444", background: "#ef444415", border: "1px solid #ef444430", borderRadius: 20, padding: "4px 10px" },
  lastRunText: { fontSize: 11, color: "#334155" },
  body: { display: "flex", gap: 0 },
  leftCol: { flex: 1, padding: "20px 24px", borderRight: "1px solid #111827", display: "flex", flexDirection: "column", gap: 20 },
  rightCol: { width: 280, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 12, flexShrink: 0 },
  section: { display: "flex", flexDirection: "column", gap: 10 },
  sectionTitle: { fontSize: 10, fontWeight: 700, color: "#334155", letterSpacing: "0.08em", textTransform: "uppercase" },
  scheduleRow: { display: "flex", gap: 6, flexWrap: "wrap" },
  scheduleBtn: { padding: "6px 14px", background: "#111827", border: "1px solid #1f2937", borderRadius: 20, color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  scheduleBtnActive: { background: "#1e1b4b", borderColor: "#6366f160", color: "#a5b4fc" },
  nextRun: { display: "flex", gap: 8, alignItems: "center" },
  nextRunLabel: { fontSize: 11, color: "#334155" },
  nextRunValue: { fontSize: 11, color: "#94a3b8", fontFamily: "inherit" },
  dataTypes: { display: "flex", flexDirection: "column", gap: 6 },
  dataType: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#64748b" },
  dataTypeDot: { width: 5, height: 5, borderRadius: "50%", background: "#334155", flexShrink: 0 },
  runBtn: { width: "100%", padding: "13px", background: "linear-gradient(135deg, #7c3aed, #4f46e5)", border: "none", borderRadius: 9, color: "#fff", fontSize: 14, fontWeight: 700, boxShadow: "0 4px 14px #7c3aed30" },
  runBtnInner: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  logsToggle: { width: "100%", padding: "8px", background: "none", border: "1px solid #1f2937", borderRadius: 7, color: "#475569", fontSize: 12, cursor: "pointer" },
  logPanel: { background: "#080a10", border: "1px solid #111827", borderRadius: 8, padding: "12px", display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" },
  logEmpty: { fontSize: 11, color: "#334155", textAlign: "center", padding: "12px 0" },
  logEntry: { display: "flex", gap: 10, alignItems: "flex-start" },
  logTime: { fontSize: 10, color: "#334155", flexShrink: 0, marginTop: 1, fontFamily: "inherit" },
  logMsg: { fontSize: 11, lineHeight: 1.5, fontFamily: "inherit" },
  schedFields: { display: "flex", gap: 14, flexWrap: "wrap", marginTop: 4 },
  schedField: { display: "flex", flexDirection: "column", gap: 5 },
  schedLabel: { fontSize: 10, fontWeight: 700, color: "#475569", letterSpacing: "0.08em", textTransform: "uppercase" },
  schedInput: { background: "#111827", border: "1px solid #1f2937", borderRadius: 7, padding: "8px 10px", color: "#e2e8f0", fontSize: 13, outline: "none", fontFamily: "inherit", width: 130 },
  schedSelect: { background: "#111827", border: "1px solid #1f2937", borderRadius: 7, padding: "8px 10px", color: "#e2e8f0", fontSize: 13, outline: "none", fontFamily: "inherit", width: 160 },
  schedHint: { fontSize: 10, color: "#334155" },
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CredentialManagement() {
  const [activeTab, setActiveTab] = useState("credentials");

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.breadcrumb}>Settings / Integrations</div>
          <h1 style={styles.title}>Integration Management</h1>
          <p style={styles.subtitle}>
            Securely connect CISO Advisor to your cloud and security tool accounts for automated data collection.
            All credentials are encrypted at rest and never transmitted in plain text.
          </p>
        </div>
      </div>

      <div style={styles.securityBanner}>
        <div style={styles.securityBannerInner}>
          <span style={styles.securityBannerIcon}>🛡</span>
          <div>
            <div style={styles.securityBannerTitle}>Read-Only Access Required</div>
            <div style={styles.securityBannerText}>
              CISO Advisor only requires read-only permissions. We never write to, modify, or delete resources in your connected accounts.
              Credentials are stored using AES-256 encryption and accessed only during scheduled collection jobs.
            </div>
          </div>
        </div>
      </div>

      <div style={styles.tabBar}>
        {[["credentials", "🔑 Credentials"], ["jobs", "⏰ Collection Jobs"]].map(([t, label]) => (
          <button key={t}
            style={{ ...styles.tab, ...(activeTab === t ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(t)}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === "credentials" ? (
        <div style={styles.content}>
          {Object.values(INTEGRATIONS).map(integration => (
            <IntegrationCard key={integration.id} integration={integration} />
          ))}
          <div style={styles.comingSoonSection}>
            <div style={styles.comingSoonTitle}>Coming in Future Releases</div>
            <div style={styles.comingSoonGrid}>
              {[
                { name: "Microsoft Azure", logo: "☁", color: "#3b82f6" },
                { name: "Google Cloud Platform", logo: "☁", color: "#34d399" },
                { name: "CrowdStrike Falcon", logo: "🦅", color: "#ef4444" },
                { name: "Splunk", logo: "📊", color: "#f59e0b" },
              ].map(i => (
                <div key={i.name} style={styles.comingSoonCard}>
                  <span style={{ fontSize: 20, color: i.color }}>{i.logo}</span>
                  <span style={styles.comingSoonName}>{i.name}</span>
                  <span style={styles.comingSoonBadge}>Soon</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div style={styles.content}>
          <div style={jobStyles.intro}>
            <div style={jobStyles.introTitle}>Automated Data Collection</div>
            <div style={jobStyles.introText}>
              Schedule recurring collection jobs to keep your risk assessments current.
              Each job pulls the latest data from your connected integrations and triggers MCMC recalculation.
            </div>
          </div>
          {Object.values(COLLECTION_INTEGRATIONS).map(integ => (
            <JobCard key={integ.id} integration={integ} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: "100vh",
    background: "#080a10",
    color: "#e2e8f0",
    fontFamily: "'DM Mono', 'IBM Plex Mono', monospace",
  },
  header: {
    padding: "32px 40px 24px",
    borderBottom: "1px solid #111827",
  },
  breadcrumb: {
    fontSize: 11, color: "#334155", letterSpacing: "0.08em",
    textTransform: "uppercase", marginBottom: 8,
  },
  title: {
    fontSize: 26, fontWeight: 700, color: "#f1f5f9",
    margin: "0 0 8px", letterSpacing: "-0.02em",
    fontFamily: "'DM Sans', system-ui, sans-serif",
  },
  subtitle: { fontSize: 13, color: "#475569", margin: 0, maxWidth: 620, lineHeight: 1.6 },
  securityBanner: {
    background: "#0c1a0e", borderBottom: "1px solid #14532d40",
    borderTop: "1px solid #14532d40", padding: "14px 40px",
  },
  securityBannerInner: { display: "flex", gap: 12, alignItems: "flex-start", maxWidth: 800 },
  securityBannerIcon: { fontSize: 18, flexShrink: 0, marginTop: 1 },
  securityBannerTitle: { fontSize: 12, fontWeight: 700, color: "#22c55e", marginBottom: 3, letterSpacing: "0.04em" },
  securityBannerText: { fontSize: 12, color: "#475569", lineHeight: 1.6 },
  content: { padding: "32px 40px", display: "flex", flexDirection: "column", gap: 20 },
  card: {
    background: "#0d1017", border: "1px solid #111827",
    borderRadius: 14, overflow: "hidden",
    transition: "border-color 0.2s",
  },
  cardConnected: { borderColor: "#22c55e30" },
  cardHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    padding: "24px 28px", gap: 16,
  },
  integrationMeta: { display: "flex", gap: 16, alignItems: "flex-start", flex: 1 },
  integrationLogo: {
    width: 48, height: 48, borderRadius: 12,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 22, flexShrink: 0,
  },
  integrationName: {
    fontSize: 16, fontWeight: 700, color: "#f1f5f9",
    marginBottom: 5, fontFamily: "'DM Sans', system-ui, sans-serif",
  },
  integrationDesc: { fontSize: 12, color: "#475569", lineHeight: 1.6, maxWidth: 480 },
  cardActions: { display: "flex", gap: 10, alignItems: "center", flexShrink: 0 },
  configureBtn: {
    padding: "8px 18px",
    background: "#111827", border: "1px solid #1f2937",
    borderRadius: 8, color: "#94a3b8",
    fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  disconnectBtn: {
    padding: "8px 18px",
    background: "none", border: "1px solid #ef444440",
    borderRadius: 8, color: "#ef4444",
    fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  connectedSummary: {
    padding: "0 28px 20px",
    display: "flex", flexDirection: "column", gap: 6,
  },
  connectedRow: { display: "flex", alignItems: "center", gap: 10 },
  connectedIcon: { color: "#22c55e", fontSize: 14, fontWeight: 700 },
  connectedText: { fontSize: 12, color: "#475569", flex: 1 },
  connectedDetail: { fontSize: 11, color: "#334155", paddingLeft: 24, fontFamily: "inherit" },
  updateBtn: {
    fontSize: 11, color: "#64748b", background: "none",
    border: "1px solid #1f2937", borderRadius: 5,
    padding: "3px 10px", cursor: "pointer",
  },
  form: {
    padding: "0 28px 28px",
    borderTop: "1px solid #111827",
    display: "flex", flexDirection: "column", gap: 18,
    paddingTop: 20,
  },
  instructionsWrap: {
    border: "1px solid #1f2937", borderRadius: 10, overflow: "hidden",
  },
  instructionsToggle: {
    width: "100%", display: "flex", justifyContent: "space-between",
    alignItems: "center", padding: "12px 16px",
    background: "#111827", border: "none", cursor: "pointer",
    color: "#94a3b8", fontSize: 13, fontWeight: 600,
  },
  instructionsBody: {
    padding: "16px", background: "#0a0d14",
    display: "flex", flexDirection: "column", gap: 14,
  },
  instructionsList: {
    margin: 0, paddingLeft: 20,
    display: "flex", flexDirection: "column", gap: 8,
  },
  instructionItem: { fontSize: 12, color: "#64748b", lineHeight: 1.6 },
  permissionsBlock: { display: "flex", flexDirection: "column", gap: 5 },
  permissionsTitle: {
    fontSize: 10, fontWeight: 700, color: "#334155",
    letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4,
  },
  permissionRow: { display: "flex", gap: 8, alignItems: "flex-start" },
  permissionCheck: { color: "#22c55e", fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1 },
  permissionText: { fontSize: 11, color: "#475569", fontFamily: "inherit" },
  docsLink: { fontSize: 12, color: "#38bdf8", textDecoration: "none", alignSelf: "flex-start" },
  securityNotice: {
    display: "flex", gap: 10, alignItems: "flex-start",
    background: "#0c1220", border: "1px solid #1e3a5f40",
    borderRadius: 8, padding: "12px 14px",
  },
  securityIcon: { fontSize: 14, flexShrink: 0 },
  securityText: { fontSize: 11, color: "#475569", lineHeight: 1.6 },
  fieldsGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr",
    gap: 16,
  },
  fieldWrap: { display: "flex", flexDirection: "column", gap: 5 },
  fieldLabel: {
    fontSize: 10, fontWeight: 700, color: "#475569",
    letterSpacing: "0.08em", textTransform: "uppercase",
  },
  fieldInput: {
    background: "#111827", border: "1px solid #1f2937",
    borderRadius: 7, padding: "10px 12px",
    color: "#e2e8f0", fontSize: 13, outline: "none",
    fontFamily: "inherit",
    width: "100%", boxSizing: "border-box",
  },
  fieldSelect: {
    background: "#111827", border: "1px solid #1f2937",
    borderRadius: 7, padding: "10px 12px",
    color: "#e2e8f0", fontSize: 13, outline: "none",
    fontFamily: "inherit",
    width: "100%", boxSizing: "border-box",
  },
  fieldHint: { fontSize: 10, color: "#334155" },
  passwordWrap: { position: "relative" },
  eyeBtn: {
    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
    background: "none", border: "none", cursor: "pointer", fontSize: 14,
  },
  successBanner: {
    background: "#0c1a0e", border: "1px solid #22c55e30",
    borderRadius: 8, padding: "10px 14px",
    fontSize: 13, color: "#22c55e", fontWeight: 600,
    display: "flex", gap: 8,
  },
  errorBanner: {
    background: "#1a0c0c", border: "1px solid #ef444430",
    borderRadius: 8, padding: "10px 14px",
    fontSize: 13, color: "#ef4444",
    display: "flex", gap: 8,
  },
  formActions: { display: "flex", gap: 10, justifyContent: "flex-end" },
  cancelBtn: {
    padding: "9px 18px", background: "none",
    border: "1px solid #1f2937", borderRadius: 8,
    color: "#64748b", fontSize: 13, cursor: "pointer",
  },
  testBtn: {
    padding: "9px 20px",
    background: "#111827", border: "1px solid #1f2937",
    borderRadius: 8, color: "#94a3b8",
    fontSize: 13, fontWeight: 700, cursor: "pointer",
  },
  saveBtn: {
    padding: "9px 22px",
    background: "linear-gradient(135deg, #15803d, #166534)",
    border: "none", borderRadius: 8,
    color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
    boxShadow: "0 4px 12px #22c55e20",
  },

  tabBar: {
    display: "flex", borderBottom: "1px solid #111827",
    padding: "0 40px", background: "#06080d",
  },
  tab: {
    padding: "13px 20px", background: "none",
    border: "none", borderBottom: "2px solid transparent",
    color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  tabActive: { color: "#a78bfa", borderBottomColor: "#7c3aed" },
  comingSoonSection: { marginTop: 8 },
  comingSoonTitle: {
    fontSize: 10, fontWeight: 700, color: "#334155",
    letterSpacing: "0.1em", textTransform: "uppercase",
    marginBottom: 14,
  },
  comingSoonGrid: {
    display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12,
  },
  comingSoonCard: {
    background: "#0d1017", border: "1px solid #111827",
    borderRadius: 10, padding: "16px",
    display: "flex", alignItems: "center", gap: 10,
    opacity: 0.5,
  },
  comingSoonName: { fontSize: 12, color: "#64748b", flex: 1 },
  comingSoonBadge: {
    fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
    color: "#334155", background: "#111827",
    borderRadius: 4, padding: "2px 6px",
  },
};

